<script setup lang="ts">
// A schedule's windows (schedule.ts), for a destination's form and a
// group's: typed and shown in the browser's time zone, kept as instants.
import { computed } from 'vue'
import { fromLocalInput, newWindow, scheduleError, toLocalInput, type Window } from '../../schedule'

const model = defineModel<Window[] | undefined>({ required: true })
const props = withDefaults(
    defineProps<{
        what: string // "it", "its destinations"
        label?: string
        started?: string // a window started, as its row says it
        make?: (ws: Window[], now: Date) => Window // the window "+ window" adds
    }>(),
    { label: 'Schedule', started: 'on air', make: newWindow },
)
const ws = computed(() => model.value ?? [])
const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
const err = computed(() => scheduleError(ws.value))
function set(i: number, k: 'start' | 'stop', v: string) {
    const next = ws.value.map((w) => ({ ...w }))
    next[i]![k] = fromLocalInput(v)
    model.value = next
}
function add() {
    model.value = [...ws.value, props.make(ws.value, new Date())]
}
function remove(i: number) {
    const next = ws.value.filter((_, j) => j !== i)
    model.value = next.length ? next : undefined
}
</script>

<template>
    <details :open="ws.length > 0" data-test="schedule">
        <summary>{{ label }}{{ ws.length ? ` · ${ws.length} window${ws.length > 1 ? 's' : ''}` : '' }}</summary>
        <div v-for="(w, i) in ws" :key="i" class="mb-1.5 flex items-center gap-1.5">
            <input
                type="datetime-local"
                :value="toLocalInput(w.start)"
                aria-label="start"
                data-test="window-start"
                @change="set(i, 'start', ($event.target as HTMLInputElement).value)"
            />
            <span class="text-fg-3">–</span>
            <input
                type="datetime-local"
                :value="toLocalInput(w.stop)"
                aria-label="stop"
                data-test="window-stop"
                @change="set(i, 'stop', ($event.target as HTMLInputElement).value)"
            />
            <span v-if="w.started" class="text-xs text-ok">{{ started }}</span>
            <button type="button" class="btn-ghost text-xs" title="Remove the window" @click="remove(i)">✕</button>
        </div>
        <p v-if="err" class="mt-0 mb-1.5 text-xs text-bad">{{ err }}</p>
        <button type="button" class="btn-ghost text-xs" data-test="window-add" @click="add">+ window</button>
        <p v-if="$slots.default" class="text-xs text-fg-3"><slot /></p>
        <p v-else class="text-xs text-fg-3">
            At each window's start the node starts {{ what }}, and at its stop it stops {{ what }} ({{ zone }}), even
            without central. A start or stop by hand in between stands until the next one. A window drops off once it
            has ended.
        </p>
    </details>
</template>
